import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { getStoredAuthToken } from '../../utils/authStorage';

interface AdminPayment {
  _id: string;
  userId: string;
  amount: number;
  currency: string;
  status: string;
  campaignPackageId?: string;
  campaignPackageName?: string;
  campaignAdId?: string;
  createdAt?: string;
}

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname}:5001`
    : '');

const API_ROOT = `${API_BASE.replace(/\/$/, '')}/api/payments/admin`;

export const AdminPaymentsByCampaign: React.FC = () => {
  const location = useLocation();
  const [payments, setPayments] = useState<AdminPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterPackageId, setFilterPackageId] = useState('');
  const [filterAdId, setFilterAdId] = useState('');

  useEffect(() => {
    const state = location.state as
      | { focus?: string; packageId?: string; packageName?: string }
      | undefined;
    if (state && state.focus === 'campaign-payments' && state.packageId && !filterPackageId) {
      setFilterPackageId(state.packageId);
    }
    // We intentionally do not depend on filterPackageId here to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const focusedPackage = useMemo(() => {
    const state = location.state as
      | { focus?: string; packageId?: string; packageName?: string }
      | undefined;
    if (!state || state.focus !== 'campaign-payments') return null;
    if (!state.packageId && !state.packageName) return null;
    return {
      id: state.packageId ?? '',
      name: state.packageName ?? '',
    };
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;

    const fetchPayments = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (filterPackageId.trim()) params.set('packageId', filterPackageId.trim());
        if (filterAdId.trim()) params.set('adId', filterAdId.trim());

        const res = await axios.get<AdminPayment[]>(
          params.toString() ? `${API_ROOT}?${params.toString()}` : API_ROOT,
          {
            headers: getStoredAuthToken()
              ? {
                  Authorization: `Bearer ${getStoredAuthToken()}`,
                }
              : undefined,
          },
        );
        if (!cancelled) {
          setPayments(res.data || []);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          let message =
            err &&
            typeof err === 'object' &&
            'message' in err &&
            typeof (err as { message?: unknown }).message === 'string'
              ? ((err as { message?: unknown }).message as string)
              : 'Payment analytics are not available right now.';

          const lower = message.toLowerCase();
          if (lower.includes('request failed') || lower.includes('network error')) {
            message =
              'Payment analytics are not available in this environment yet. This does not affect running campaigns.';
          }

          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchPayments();

    return () => {
      cancelled = true;
    };
  }, [filterPackageId, filterAdId]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Campaign payments</h2>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Recent payments tied to specific campaign packages or creatives.
          </p>
          {focusedPackage && (
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              Showing payments for
              {focusedPackage.name ? (
                <>
                  {' '}
                  <span className="font-medium text-slate-800 dark:text-slate-200">{focusedPackage.name}</span>
                </>
              ) : null}
              {focusedPackage.id && (
                <>
                  {' '}
                  <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-mono text-slate-600 dark:text-slate-400">
                    {focusedPackage.id}
                  </span>
                </>
              )}
            </p>
          )}
        </div>
        <form className="flex flex-wrap items-end gap-2 text-xs">
          <div>
            <label className="block text-[11px] font-medium text-slate-700 dark:text-slate-300">Package ID</label>
            <input
              type="text"
              value={filterPackageId}
              onChange={(e) => setFilterPackageId(e.target.value)}
              placeholder="optional"
              className="mt-1 w-40 rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-slate-700 dark:text-slate-300">Ad ID</label>
            <input
              type="text"
              value={filterAdId}
              onChange={(e) => setFilterAdId(e.target.value)}
              placeholder="optional"
              className="mt-1 w-40 rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </form>
      </div>

      {loading && <p className="text-xs text-slate-500 dark:text-slate-400">Loading payments…</p>}
      {error && <p className="text-xs text-slate-600 dark:text-slate-400">{error}</p>}

      {!loading && !error && payments.length === 0 && (
        <p className="text-xs text-slate-500 dark:text-slate-400">No payments found for the current filters.</p>
      )}

      {payments.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <table className="min-w-full divide-y divide-slate-200 text-xs">
            <thead className="bg-slate-50 dark:bg-slate-900">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-slate-700 dark:text-slate-300">When</th>
                <th className="px-3 py-2 text-left font-medium text-slate-700 dark:text-slate-300">User</th>
                <th className="px-3 py-2 text-left font-medium text-slate-700 dark:text-slate-300">Amount</th>
                <th className="px-3 py-2 text-left font-medium text-slate-700 dark:text-slate-300">Status</th>
                <th className="px-3 py-2 text-left font-medium text-slate-700 dark:text-slate-300">Package</th>
                <th className="px-3 py-2 text-left font-medium text-slate-700 dark:text-slate-300">Ad</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white dark:bg-slate-900">
              {payments.map((p) => (
                <tr key={p._id}>
                  <td className="whitespace-nowrap px-3 py-1.5 text-slate-700 dark:text-slate-300">
                    {p.createdAt ? new Date(p.createdAt).toLocaleString() : '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-slate-700 dark:text-slate-300">{p.userId}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-slate-700 dark:text-slate-300">
                    {p.amount.toFixed(2)} {p.currency}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${{
                        completed: 'bg-emerald-50 text-emerald-700',
                        pending: 'bg-amber-50 text-amber-700',
                        failed: 'bg-rose-50 text-rose-700',
                      }[p.status as 'completed' | 'pending' | 'failed'] || 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'}`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-slate-700 dark:text-slate-300">
                    {p.campaignPackageName || p.campaignPackageId || '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-slate-700 dark:text-slate-300">
                    {p.campaignAdId || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentsByCampaign;
